import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import LanguageSelector from "@/components/LanguageSelector";
import PermissionErrorModal from "@/components/PermissionErrorModal";
import { ONBOARDING } from "@/constants/testIds";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  ArrowRight,
  ArrowLeft,
  IdentificationBadge,
  Camera,
  ShieldCheck,
  MapPin,
  CheckCircle,
  Sparkle,
  Upload,
  Fingerprint,
  Info,
  Warning,
  SignOut,
} from "@phosphor-icons/react";

const STEPS = [
  { key: "welcome", labelKey: "onboarding.steps.welcome" },
  { key: "mobile", labelKey: "onboarding.steps.mobile" },
  { key: "id", labelKey: "onboarding.steps.id" },
  { key: "selfie", labelKey: "onboarding.steps.selfie" },
  { key: "submit", labelKey: "onboarding.steps.submit" },
  { key: "success", labelKey: "onboarding.steps.success" },
];

const ID_TYPES = [
  { id: "aadhaar", label: "Aadhaar", hint: "12-digit UIDAI card" },
  { id: "pan", label: "PAN", hint: "Permanent Account Number" },
  { id: "dl", label: "Driving Licence", hint: "State-issued DL" },
  { id: "passport", label: "Passport", hint: "Republic of India" },
];

function ProgressBar({ index }) {
  const { t } = useTranslation();
  const pct = ((index + 1) / STEPS.length) * 100;
  return (
    <div data-testid={ONBOARDING.progress} className="mb-8">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
        <span>
          {t("onboarding.stepOfN", { n: index + 1, total: STEPS.length })}
        </span>
        <span className="text-indigo-300">{t(STEPS[index].labelKey)}</span>
      </div>
      <div className="h-1 w-full rounded-full bg-white/[0.05] overflow-hidden">
        <motion.div
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="h-full bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-emerald-400"
        />
      </div>
    </div>
  );
}

function Shell({ children, index, onSignOut }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={ONBOARDING.root}
      className="min-h-screen flex flex-col items-center justify-center p-4 md:p-8 relative"
    >
      <div className="lele-grain absolute inset-0 opacity-30 pointer-events-none" />
      <div className="relative z-10 w-full max-w-lg lele-card p-6 md:p-10">
        <div className="flex items-center gap-3 mb-6">
          <BrandLogo size={34} showWordmark />
          <div className="ml-auto flex items-center gap-3">
            <LanguageSelector />
            <button
              type="button"
              data-testid="onboarding-signout-btn"
              onClick={onSignOut}
              className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 hover:text-rose-300 transition-colors flex items-center gap-1.5"
              title={t("common.signout")}
            >
              <SignOut size={12} weight="bold" />
              {t("common.signout")}
            </button>
          </div>
        </div>
        <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-indigo-300/70 text-right">
          LeLe Identity™
        </div>
        <ProgressBar index={index} />
        <AnimatePresence mode="wait">{children}</AnimatePresence>
      </div>
    </div>
  );
}

function StepMotion({ children, keyName }) {
  return (
    <motion.div
      key={keyName}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ---------- fileToBase64 ----------
async function fileToImageInfo(file) {
  if (!file) return null;
  const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  if (!allowed.includes(file.type)) {
    throw new Error("Please upload a JPG, PNG or WEBP image.");
  }
  if (file.size > 6 * 1024 * 1024) {
    throw new Error("Image is too large. Please keep it under 6 MB.");
  }
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return { base64: btoa(bin), mime: file.type };
}

// ---------- Step 1: Welcome ----------
function WelcomeStep({ onNext }) {
  const { t } = useTranslation();
  return (
    <StepMotion keyName="welcome">
      <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-3">
        {t("onboarding.welcome.eyebrow")}
      </div>
      <h2 className="font-heading text-3xl md:text-4xl leading-tight mb-3">
        {t("onboarding.welcome.title")}
      </h2>
      <p className="text-neutral-400 leading-relaxed mb-8">
        {t("onboarding.welcome.body")}
      </p>

      <div className="space-y-3 mb-8">
        <FeatureRow
          icon={<Fingerprint size={18} weight="duotone" className="text-indigo-300" />}
          title={t("onboarding.welcome.feature1Title")}
          body={t("onboarding.welcome.feature1Body")}
        />
        <FeatureRow
          icon={<Camera size={18} weight="duotone" className="text-fuchsia-300" />}
          title={t("onboarding.welcome.feature2Title")}
          body={t("onboarding.welcome.feature2Body")}
        />
        <FeatureRow
          icon={<ShieldCheck size={18} weight="duotone" className="text-emerald-300" />}
          title={t("onboarding.welcome.feature3Title")}
          body={t("onboarding.welcome.feature3Body")}
        />
      </div>

      <button
        data-testid={ONBOARDING.welcomeStartBtn}
        onClick={onNext}
        className="lele-btn-primary w-full flex items-center justify-center gap-2 group"
      >
        <Sparkle size={16} weight="fill" />
        {t("onboarding.welcome.cta")}
        <ArrowRight
          size={16}
          weight="bold"
          className="transition-transform group-hover:translate-x-1"
        />
      </button>
    </StepMotion>
  );
}

function FeatureRow({ icon, title, body }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl border border-white/[0.06] bg-white/[0.02]">
      <div className="h-8 w-8 rounded-lg bg-white/[0.04] grid place-items-center shrink-0">
        {icon}
      </div>
      <div className="text-sm">
        <div className="text-neutral-100 font-medium">{title}</div>
        <div className="text-neutral-500 text-[13px] leading-snug">{body}</div>
      </div>
    </div>
  );
}

// ---------- Step 2: Mobile ----------
function MobileStep({ user, onDone }) {
  const alreadyVerified = !!user?.phone;
  const [phone, setPhone] = useState(user?.phone?.replace("+91", "") || "");
  const [step, setStep] = useState("phone");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  if (alreadyVerified) {
    return (
      <StepMotion keyName="mobile-verified">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-300 mb-3">
          Step 2 · Mobile
        </div>
        <h2 className="font-heading text-3xl mb-3">Your mobile is already verified</h2>
        <p className="text-neutral-400 mb-6">
          We'll use{" "}
          <span className="text-neutral-100 font-medium">{user.phone}</span> as your
          primary contact.
        </p>
        <button
          data-testid={ONBOARDING.mobileContinueBtn}
          onClick={() => onDone({ phone: user.phone })}
          className="lele-btn-primary w-full flex items-center justify-center gap-2"
        >
          Continue <ArrowRight size={16} weight="bold" />
        </button>
      </StepMotion>
    );
  }

  const send = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/auth/otp/send", { phone });
      setStep("code");
      if (data?.dev_code) {
        setDevCode(data.dev_code);
        toast.success(`OTP sent (dev mode: ${data.dev_code})`);
      } else {
        toast.success("OTP sent");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const verify = async (full) => {
    const c = (full ?? code).trim();
    if (c.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setVerifying(true);
    try {
      // Verify OTP but don't overwrite session — we already have an authed user.
      // Instead just mark the phone as verified and pass forward.
      const digits = phone.replace(/\D/g, "");
      const normalized = digits.length === 10 ? `+91${digits}` : phone;
      onDone({ phone: normalized, code: c });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <StepMotion keyName="mobile">
      <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-3">
        Step 2 · Verify mobile
      </div>
      <h2 className="font-heading text-3xl mb-3">Link your number</h2>
      <p className="text-neutral-400 mb-6">
        We need a live number so consent, reminders and legal notices reach you
        instantly.
      </p>

      {step === "phone" ? (
        <>
          <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
            Mobile number
          </label>
          <div className="flex gap-2 mb-5">
            <div className="lele-input rounded-lg px-3 py-3 text-sm text-neutral-300 flex items-center">
              +91
            </div>
            <input
              data-testid={ONBOARDING.mobilePhoneInput}
              type="tel"
              inputMode="numeric"
              className="lele-input flex-1 rounded-lg px-4 py-3"
              placeholder="9876543210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <button
            data-testid={ONBOARDING.mobileSendOtpBtn}
            onClick={send}
            disabled={sending}
            className="lele-btn-primary w-full flex items-center justify-center gap-2"
          >
            {sending ? "Sending…" : "Send OTP"}
            {!sending && <ArrowRight size={16} weight="bold" />}
          </button>
          <div className="mt-4 text-[11px] text-neutral-500 flex items-start gap-2 leading-relaxed">
            <Info size={13} weight="duotone" className="text-indigo-400 mt-0.5 shrink-0" />
            Dev-mode OTP: the code appears on-screen instead of by SMS.
          </div>
        </>
      ) : (
        <>
          <button
            onClick={() => {
              setStep("phone");
              setCode("");
              setDevCode("");
            }}
            className="text-xs text-neutral-500 hover:text-neutral-100 flex items-center gap-1 mb-4"
            type="button"
          >
            <ArrowLeft size={12} weight="bold" /> Change number
          </button>
          <div className="text-sm text-neutral-300 mb-1">
            Sent to <span className="text-neutral-100">+91 {phone.replace(/\D/g, "")}</span>
          </div>
          <div className="text-[11px] text-neutral-500 mb-4">
            Enter the 6-digit code below.
          </div>
          {devCode && (
            <div className="mb-5 rounded-xl border border-indigo-400/30 bg-indigo-500/[0.06] px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-300 mb-1">
                Dev-mode OTP
              </div>
              <div className="font-mono text-xl tracking-[0.4em] text-indigo-200">
                {devCode}
              </div>
            </div>
          )}
          <div className="flex justify-center mb-6">
            <InputOTP
              data-testid={ONBOARDING.mobileCodeInput}
              maxLength={6}
              value={code}
              onChange={(v) => {
                setCode(v);
                if (v.length === 6) verify(v);
              }}
              containerClassName="gap-2"
            >
              <InputOTPGroup className="gap-2">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    className="!h-12 !w-11 !border !rounded-lg lele-input text-lg font-heading text-neutral-100"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <button
            data-testid={ONBOARDING.mobileVerifyBtn}
            onClick={() => verify()}
            disabled={verifying || code.length !== 6}
            className="lele-btn-primary w-full flex items-center justify-center gap-2"
          >
            {verifying ? "Verifying…" : "Verify & continue"}
            {!verifying && <ArrowRight size={16} weight="bold" />}
          </button>
        </>
      )}
    </StepMotion>
  );
}

// ---------- Step 3: ID upload ----------
function IdStep({ onDone }) {
  const [idType, setIdType] = useState("aadhaar");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [storagePath, setStoragePath] = useState("");
  const [imageBase64, setImageBase64] = useState("");
  const [imageMime, setImageMime] = useState("image/jpeg");
  const fileRef = useRef(null);
  // Editable fields
  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [address, setAddress] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const info = await fileToImageInfo(file);
      setPreview(URL.createObjectURL(file));
      setImageBase64(info.base64);
      setImageMime(info.mime);
      const { data } = await api.post("/identity/upload-id", {
        image_base64: info.base64,
        mime_type: info.mime,
      });
      setStoragePath(data.storage_path);
      const ex = data.extracted || {};
      setExtracted(ex);
      setFullName(ex.full_name || "");
      setDob(ex.dob || "");
      setGender(ex.gender || "");
      setAddress(ex.address || "");
      if (ex.id_type && ex.id_type !== "unknown") setIdType(ex.id_type);
      toast.success("ID scanned. Please review the details.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const canContinue =
    storagePath && fullName.trim() && !uploading && idType;

  return (
    <StepMotion keyName="id">
      <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-3">
        Step 3 · Verify identity
      </div>
      <h2 className="font-heading text-3xl mb-3">Upload a government ID</h2>
      <p className="text-neutral-400 mb-5">
        Pick one — we store only a masked reference (e.g. XXXX-XXXX-1234).
      </p>

      <div className="grid grid-cols-2 gap-2 mb-5">
        {ID_TYPES.map((t) => (
          <button
            key={t.id}
            data-testid={ONBOARDING.idTypeSelect(t.id)}
            onClick={() => setIdType(t.id)}
            type="button"
            className={`text-left px-4 py-3 rounded-xl border transition-colors ${
              idType === t.id
                ? "border-indigo-400/60 bg-indigo-500/[0.08]"
                : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
            }`}
          >
            <div className="text-sm font-medium text-neutral-100">{t.label}</div>
            <div className="text-[11px] text-neutral-500">{t.hint}</div>
          </button>
        ))}
      </div>

      <label
        htmlFor="id-file"
        className="block border border-dashed border-white/10 rounded-xl p-6 text-center cursor-pointer hover:bg-white/[0.02] mb-5"
      >
        {preview ? (
          <div className="space-y-2">
            <img
              src={preview}
              alt="ID preview"
              className="mx-auto max-h-40 rounded-lg border border-white/10"
            />
            <div className="text-[11px] text-neutral-500">
              Tap to change photo
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-neutral-400">
            <Upload size={22} weight="duotone" className="text-indigo-300" />
            <div className="text-sm">
              {uploading ? "Scanning your ID…" : "Tap to upload or take a photo"}
            </div>
            <div className="text-[11px] text-neutral-500">
              JPG, PNG or WEBP · up to 6 MB
            </div>
          </div>
        )}
        <input
          data-testid={ONBOARDING.idFileInput}
          id="id-file"
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onFile}
          className="hidden"
        />
      </label>

      {extracted && (
        <>
          {extracted.id_number_masked && (
            <div className="mb-4 rounded-xl border border-emerald-400/25 bg-emerald-500/[0.04] px-4 py-3 text-xs text-emerald-200">
              Detected ID reference:{" "}
              <span className="font-mono tracking-widest">
                {extracted.id_number_masked}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 mb-2">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                Full legal name (as printed on ID) *
              </label>
              <input
                data-testid={ONBOARDING.idFullName}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                readOnly={!!(extracted && extracted.full_name)}
                className={`lele-input w-full rounded-lg px-4 py-2.5 text-sm ${
                  extracted && extracted.full_name
                    ? "cursor-not-allowed opacity-90"
                    : ""
                }`}
                placeholder="Auto-filled from your ID"
              />
              <div className="text-[10px] text-neutral-500 mt-1">
                {extracted && extracted.full_name
                  ? "Locked to the name printed on your ID. This ensures every agreement carries a legally verifiable name."
                  : "Please type it exactly as printed on the ID."}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                  DOB
                </label>
                <input
                  data-testid={ONBOARDING.idDob}
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  className="lele-input w-full rounded-lg px-4 py-2.5 text-sm"
                  placeholder="YYYY-MM-DD"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                  Gender
                </label>
                <input
                  data-testid={ONBOARDING.idGender}
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="lele-input w-full rounded-lg px-4 py-2.5 text-sm"
                  placeholder="male / female / other"
                />
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                Address
              </label>
              <textarea
                data-testid={ONBOARDING.idAddress}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={2}
                className="lele-input w-full rounded-lg px-4 py-2.5 text-sm"
                placeholder="Extracted from ID"
              />
            </div>
          </div>
        </>
      )}

      <button
        data-testid={ONBOARDING.idContinueBtn}
        disabled={!canContinue}
        onClick={() =>
          onDone({
            id_type: idType,
            full_name: fullName.trim(),
            dob: dob.trim(),
            gender: gender.trim(),
            address: address.trim(),
            id_storage_path: storagePath,
            id_number_masked: extracted?.id_number_masked || "",
            id_number_last4: extracted?.id_number_last4 || "",
            id_image_base64: imageBase64,
            id_image_mime: imageMime,
          })
        }
        className="lele-btn-primary w-full mt-6 flex items-center justify-center gap-2"
      >
        Continue <ArrowRight size={16} weight="bold" />
      </button>
    </StepMotion>
  );
}

// ---------- Step 4: Selfie + GPS ----------
function SelfieStep({ idStoragePath, onDone }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [captured, setCaptured] = useState(null); // {base64, dataUrl}
  const [uploading, setUploading] = useState(false);
  const [faceInfo, setFaceInfo] = useState(null);
  const [storagePath, setStoragePath] = useState("");
  const [gps, setGps] = useState(null);
  const [gpsError, setGpsError] = useState("");
  const [locating, setLocating] = useState(false);
  const [permError, setPermError] = useState(null); // "camera" | "location" | null

  // Any permission-style error triggers the branded modal. On Android an
  // overlay app can cause this without ever showing a prompt.
  const isPermissionError = (err) => {
    if (!err) return false;
    const name = err.name || "";
    const msg = (err.message || "").toLowerCase();
    return (
      name === "NotAllowedError" ||
      name === "SecurityError" ||
      name === "AbortError" ||
      /permission|denied|dismissed|overlay|blocked/.test(msg)
    );
  };

  const startCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStreaming(true);
    } catch (err) {
      if (isPermissionError(err)) {
        setPermError("camera");
      } else {
        toast.error(err?.message || t("permErr.camera.title"));
      }
    }
  };

  const stopCam = () => {
    const stream = videoRef.current?.srcObject;
    stream?.getTracks?.().forEach((t) => t.stop());
    setStreaming(false);
  };

  useEffect(() => () => stopCam(), []);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const size = Math.min(video.videoWidth, video.videoHeight, 720);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    const base64 = dataUrl.split(",")[1];
    setCaptured({ base64, dataUrl });
    stopCam();
  };

  const retake = () => {
    setCaptured(null);
    setFaceInfo(null);
    setStoragePath("");
    startCam();
  };

  const useSelfie = async () => {
    if (!captured) return;
    setUploading(true);
    try {
      const { data } = await api.post("/identity/upload-selfie", {
        image_base64: captured.base64,
        mime_type: "image/jpeg",
        id_storage_path: idStoragePath || undefined,
      });
      setStoragePath(data.storage_path);
      setFaceInfo(data.face_match);
      toast.success("Selfie captured");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const requestLocation = () => {
    if (!("geolocation" in navigator)) {
      setGpsError("Geolocation is not supported in this browser");
      return;
    }
    setLocating(true);
    setGpsError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setLocating(false);
        toast.success("Location captured");
      },
      (err) => {
        setLocating(false);
        // code 1 = PERMISSION_DENIED, code 2 = POSITION_UNAVAILABLE (also often
        // caused by Android overlays), code 3 = TIMEOUT.
        if (err && (err.code === 1 || err.code === 2)) {
          setPermError("location");
          setGpsError("");
        } else {
          setGpsError(err?.message || "Location permission denied");
        }
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const canContinue = captured && storagePath && !uploading;

  return (
    <StepMotion keyName="selfie">
      <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-3">
        Step 4 · Selfie & location
      </div>
      <h2 className="font-heading text-3xl mb-3">Prove you're here</h2>
      <p className="text-neutral-400 mb-5">
        A live selfie + your device location gives every future agreement
        cryptographic proof-of-presence.
      </p>

      <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] bg-black/40 aspect-square mb-4">
        {captured ? (
          <img src={captured.dataUrl} alt="Selfie preview" className="w-full h-full object-cover" />
        ) : (
          <video
            ref={videoRef}
            playsInline
            muted
            className="w-full h-full object-cover"
          />
        )}
        <canvas ref={canvasRef} className="hidden" />
        {!streaming && !captured && (
          <button
            onClick={startCam}
            data-testid={ONBOARDING.selfieCaptureBtn}
            type="button"
            className="absolute inset-0 grid place-items-center bg-black/60 hover:bg-black/50 transition-colors"
          >
            <div className="flex flex-col items-center gap-2 text-neutral-100">
              <Camera size={26} weight="duotone" className="text-indigo-300" />
              <span className="text-sm">Tap to enable camera</span>
            </div>
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {streaming && !captured && (
          <button
            data-testid={ONBOARDING.selfieCaptureBtn}
            onClick={capture}
            className="lele-btn-primary flex-1 flex items-center justify-center gap-2"
          >
            <Camera size={16} weight="fill" /> Capture selfie
          </button>
        )}
        {captured && !storagePath && (
          <>
            <button
              data-testid={ONBOARDING.selfieRetakeBtn}
              onClick={retake}
              className="lele-btn-ghost flex-1 flex items-center justify-center gap-2"
            >
              Retake
            </button>
            <button
              data-testid={ONBOARDING.selfieUseBtn}
              onClick={useSelfie}
              disabled={uploading}
              className="lele-btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {uploading ? "Uploading…" : "Use this photo"}
            </button>
          </>
        )}
      </div>

      {faceInfo && (
        <div
          className={`mb-4 rounded-xl border px-4 py-3 text-xs ${
            faceInfo.match
              ? "border-emerald-400/25 bg-emerald-500/[0.05] text-emerald-200"
              : "border-amber-400/25 bg-amber-500/[0.05] text-amber-200"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            {faceInfo.match ? (
              <ShieldCheck size={14} weight="fill" />
            ) : (
              <Warning size={14} weight="fill" />
            )}
            <span className="font-medium">
              Face match {(faceInfo.score * 100).toFixed(0)}%
            </span>
          </div>
          <div className="text-[11px] opacity-80 leading-snug">
            {faceInfo.reason || (faceInfo.match ? "Matches your ID photo." : "Weak match — you can still continue.")}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-lg bg-fuchsia-500/10 border border-fuchsia-400/30 grid place-items-center">
            <MapPin size={16} weight="duotone" className="text-fuchsia-300" />
          </div>
          <div className="flex-1">
            <div className="text-sm text-neutral-100 font-medium">
              Device location
            </div>
            {gps ? (
              <div className="text-[11px] text-neutral-500 mt-1">
                {gps.lat.toFixed(4)}, {gps.lng.toFixed(4)} · ±{Math.round(gps.accuracy)}m
              </div>
            ) : (
              <div className="text-[11px] text-neutral-500 mt-1">
                Not shared yet — helps prove where you signed.
              </div>
            )}
            {gpsError && (
              <div className="text-[11px] text-amber-400 mt-1">{gpsError}</div>
            )}
          </div>
          <button
            data-testid={ONBOARDING.requestLocationBtn}
            onClick={requestLocation}
            type="button"
            disabled={locating}
            className="lele-btn-ghost text-xs"
          >
            {gps ? "Update" : locating ? "Locating…" : "Share"}
          </button>
        </div>
      </div>

      <button
        data-testid={ONBOARDING.selfieContinueBtn}
        disabled={!canContinue}
        onClick={() =>
          onDone({
            selfie_storage_path: storagePath,
            face_match_score: faceInfo?.score || 0,
            face_matched: !!faceInfo?.match,
            gps,
          })
        }
        className="lele-btn-primary w-full flex items-center justify-center gap-2"
      >
        Continue <ArrowRight size={16} weight="bold" />
      </button>

      <PermissionErrorModal
        open={!!permError}
        kind={permError || "camera"}
        onClose={() => setPermError(null)}
        onRetry={() => {
          const kind = permError;
          setPermError(null);
          if (kind === "camera") startCam();
          else if (kind === "location") requestLocation();
        }}
      />
    </StepMotion>
  );
}

// ---------- Step 5: Submit ----------
function SubmitStep({ payload, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const device = {
        user_agent: navigator.userAgent,
        platform: navigator.platform || "",
        screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
        timezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      };
      const body = { ...payload, device };
      await api.post("/identity/complete", body);
      onSuccess();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepMotion keyName="submit">
      <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-3">
        Step 5 · Final check
      </div>
      <h2 className="font-heading text-3xl mb-3">Ready to lock this in?</h2>
      <p className="text-neutral-400 mb-6">
        Review the summary below. Once submitted, your identity block is
        attached to every loan you create.
      </p>

      <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] divide-y divide-white/[0.05] mb-6 text-sm">
        <Row k="ID type" v={payload.id_type?.toUpperCase()} />
        <Row k="Full name" v={payload.full_name} />
        <Row k="DOB" v={payload.dob || "—"} />
        <Row k="Gender" v={payload.gender || "—"} />
        <Row k="ID reference" v={payload.id_number_masked || "—"} />
        <Row
          k="Face match"
          v={
            payload.face_matched
              ? `Verified · ${(payload.face_match_score * 100).toFixed(0)}%`
              : `Best-effort · ${(payload.face_match_score * 100).toFixed(0)}%`
          }
        />
        <Row
          k="Location"
          v={
            payload.gps
              ? `${payload.gps.lat.toFixed(4)}, ${payload.gps.lng.toFixed(4)}`
              : "Not shared"
          }
        />
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-400/25 bg-rose-500/[0.05] px-4 py-3 text-xs text-rose-200">
          {error}
        </div>
      )}

      <button
        data-testid={ONBOARDING.submitBtn}
        onClick={submit}
        disabled={saving}
        className="lele-btn-primary w-full flex items-center justify-center gap-2"
      >
        {saving ? "Creating your master profile…" : "Create master profile"}
        {!saving && <ShieldCheck size={16} weight="fill" />}
      </button>
    </StepMotion>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">{k}</div>
      <div className="text-right text-neutral-100 text-sm max-w-[65%] break-words">
        {v || "—"}
      </div>
    </div>
  );
}

// ---------- Step 6: Success ----------
function SuccessStep({ onDone }) {
  return (
    <StepMotion keyName="success">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: "backOut" }}
        className="mx-auto mb-6 h-20 w-20 rounded-full bg-emerald-500/10 border border-emerald-400/30 grid place-items-center"
      >
        <CheckCircle size={38} weight="duotone" className="text-emerald-300" />
      </motion.div>
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-300 mb-2">
          Master profile created
        </div>
        <h2 className="font-heading text-3xl mb-3">You're verified on LeLe™</h2>
        <p className="text-neutral-400 mb-8">
          Every loan you create now carries a signed identity block. Welcome to
          the trust ledger.
        </p>
        <button
          data-testid={ONBOARDING.goToDashboardBtn}
          onClick={onDone}
          className="lele-btn-primary w-full flex items-center justify-center gap-2"
        >
          Go to dashboard <ArrowRight size={16} weight="bold" />
        </button>
      </div>
    </StepMotion>
  );
}

// ---------- Master page ----------
export default function OnboardingPage() {
  const { user, refresh, ready, logout } = useAuth();
  const nav = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [payload, setPayload] = useState({});

  useEffect(() => {
    if (ready && user?.identity_complete) {
      nav("/", { replace: true });
    }
  }, [ready, user, nav]);

  const handleSignOut = async () => {
    try {
      await logout();
    } catch {
      /* ignore */
    }
    nav("/login", { replace: true });
  };

  const goNext = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));

  const renderStep = () => {
    switch (stepIdx) {
      case 0:
        return <WelcomeStep onNext={goNext} />;
      case 1:
        return (
          <MobileStep
            user={user}
            onDone={(m) => {
              setPayload((p) => ({ ...p, phone: m.phone }));
              goNext();
            }}
          />
        );
      case 2:
        return (
          <IdStep
            onDone={(idData) => {
              setPayload((p) => ({ ...p, ...idData }));
              goNext();
            }}
          />
        );
      case 3:
        return (
          <SelfieStep
            idStoragePath={payload.id_storage_path}
            onDone={(sData) => {
              setPayload((p) => ({ ...p, ...sData }));
              goNext();
            }}
          />
        );
      case 4:
        return (
          <SubmitStep
            payload={payload}
            onSuccess={async () => {
              await refresh();
              goNext();
            }}
          />
        );
      case 5:
        return <SuccessStep onDone={() => nav("/", { replace: true })} />;
      default:
        return null;
    }
  };

  return <Shell index={stepIdx} onSignOut={handleSignOut}>{renderStep()}</Shell>;
}
